/**
 * Open Code Registry 客户端（组件分发，ARCHITECTURE_DESIGN §6.1）。
 *
 * 拉取远程组件清单 / 单个组件完整定义（含 swizzle 源码）；API 不可用时静默降级
 * （清单返回空列表、单个组件返回 null），组件面板不阻塞。
 */

import { apiClient } from "@/services/api";
import type { PropSchema } from "@shared/types";

export interface RegistryComponentSummary {
  type: string;
  label: string;
  category: string;
  description?: string;
  propSchema: PropSchema[];
  registryUrl?: string;
  swizzleEnabled?: boolean;
}

export interface RegistryComponent extends RegistryComponentSummary {
  sourceAvailable: boolean;
  source: string | null;
}

/** 拉取组件清单；API 不可用返回空列表（降级）。 */
export async function fetchRegistryComponents(): Promise<RegistryComponentSummary[]> {
  try {
    const response = await apiClient.get("/registry/components");
    const data = response.data as RegistryComponentSummary[];
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/** 拉取单个组件完整定义（含渲染器源码）；API 不可用 / 404 返回 null（降级）。 */
export async function fetchRegistryComponent(type: string): Promise<RegistryComponent | null> {
  try {
    const response = await apiClient.get(`/registry/components/${encodeURIComponent(type)}`);
    return response.data as RegistryComponent;
  } catch {
    return null;
  }
}

/** 安装到项目：把组件源码下载为独立 .tsx 文件（swizzle eject）。 */
export function downloadComponentSource(component: RegistryComponent): void {
  if (!component.source) return;
  const blob = new Blob([component.source], { type: "text/typescript;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${component.type}.tsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
